/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { AlertOctagon, CheckCircle2, Package } from 'lucide-react';
import { AxiosError } from 'axios';

import Modal from '@components/atoms/modals/modal';
import Button from '@components/atoms/ui-kit/button';
import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import DateInput from '@components/atoms/ui-kit/date-input';
import SelectInput from '@components/atoms/ui-kit/select-input';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useDownloadOnPremBundle } from '@utils/api/back-office/licensing';
import { useGetAllLanguages } from '@utils/api/back-office/settings/use-get-all-languages';
import { BaseError } from '@utils/types/shared/base';

const REALTIME_CAPABLE_BCP47 = [
  'ar-ar',
  'da-dk',
  'de-de',
  'en-us',
  'es-es',
  'fi-fi',
  'fr-fr',
  'it-it',
  'nb-no',
  'nl-be',
  'nl-nl',
  'nl-pol',
  'sv-se',
  'tl-ph'
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  licensee: string;
  org_limit: number;
  user_limit: number;
  max_processing: number;
  expires: string;
  realtime: boolean;
  max_realtime_streams: number | null;
  realtime_languages: { label: string; value: string }[];
}

// Try to extract a human-readable message from a blob error body (Axios delivers
// blob-typed error responses as Blob objects instead of parsed JSON).
const readBlobError = async (data: unknown): Promise<string> => {
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as Record<string, string>;
      return (
        parsed.error_description ||
        parsed.message ||
        parsed.detail ||
        text.slice(0, 200)
      );
    } catch {
      return '';
    }
  }
  return '';
};

export default function DownloadOnPremBundleModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const downloadMutation = useDownloadOnPremBundle();
  const { data: languagesData, isLoading: languagesLoading } =
    useGetAllLanguages();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bundleDownloaded, setBundleDownloaded] = useState(false);
  const [bundleFilename, setBundleFilename] = useState('onprem-bundle.zip');
  const [bundleHadRealtime, setBundleHadRealtime] = useState(false);
  const [submitError, setSubmitError] = useState<{
    status: number | null;
    url: string;
    detail: string;
    hint?: string;
  } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      licensee: '',
      org_limit: 1,
      user_limit: 10,
      max_processing: 3,
      expires: '',
      realtime: false,
      max_realtime_streams: null,
      realtime_languages: []
    }
  });

  const realtimeEnabled = watch('realtime');
  const selectedLangs = watch('realtime_languages');

  const languageOptions = useMemo(() => {
    const items = languagesData?.result ?? [];
    return items
      .filter(
        l =>
          l.is_enabled !== false &&
          !l.is_private &&
          REALTIME_CAPABLE_BCP47.includes(l.bcp47)
      )
      .map(l => ({
        label: l.name ? `${l.name} (${l.bcp47})` : l.bcp47,
        value: l.bcp47
      }));
  }, [languagesData]);

  const { expiresMin, expiresMax } = useMemo(() => {
    const today = new Date();
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    const maxDate = new Date(today);
    maxDate.setFullYear(maxDate.getFullYear() + 5);
    return { expiresMin: iso(today), expiresMax: iso(maxDate) };
  }, []);

  const allSelected =
    languageOptions.length > 0 &&
    selectedLangs.length === languageOptions.length;

  const handleToggleSelectAll = () => {
    setValue('realtime_languages', allSelected ? [] : [...languageOptions], {
      shouldDirty: true
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setBundleDownloaded(false);
      setBundleFilename('onprem-bundle.zip');
      setBundleHadRealtime(false);
      setSubmitError(null);
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: FormData) => {
    if (!data.licensee.trim()) return;

    if (data.realtime && data.realtime_languages.length === 0) {
      setSubmitError({
        status: null,
        url: 'api/admin/licensing/on-prem-bundle',
        detail:
          t('realtime-languages-required') ||
          'Pick at least one realtime language, or turn off the Realtime ASR add-on.'
      });
      return;
    }
    if (
      data.realtime &&
      (data.max_realtime_streams === null ||
        data.max_realtime_streams === undefined ||
        data.max_realtime_streams < 1)
    ) {
      setSubmitError({
        status: null,
        url: 'api/admin/licensing/on-prem-bundle',
        detail:
          t('realtime-streams-required') ||
          'Set the max concurrent realtime streams (≥1), or turn off the Realtime ASR add-on.'
      });
      return;
    }

    const expiresIso = data.expires
      ? new Date(`${data.expires}T23:59:59Z`).toISOString()
      : null;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const filename = await downloadMutation.mutateAsync({
        licensee: data.licensee.trim(),
        org_limit: data.org_limit,
        user_limit: data.user_limit,
        max_processing: data.max_processing,
        expires: expiresIso,
        realtime: data.realtime,
        max_realtime_streams: data.realtime
          ? (data.max_realtime_streams ?? null)
          : null,
        realtime_languages: data.realtime
          ? data.realtime_languages.map(o => o.value)
          : null
      });
      setBundleFilename(filename);
      setBundleHadRealtime(data.realtime);
      setBundleDownloaded(true);
    } catch (err) {
      const axiosErr = err as AxiosError<BaseError>;
      const status = axiosErr.response?.status ?? null;
      const body = axiosErr.response?.data;
      const fullUrl = [
        axiosErr.config?.baseURL?.replace(/\/$/, ''),
        axiosErr.config?.url?.replace(/^\//, '')
      ]
        .filter(Boolean)
        .join('/');

      // Blob error bodies need async text extraction.
      const blobDetail = await readBlobError(body);
      const parsedBody = body as Record<string, string> | undefined;
      const detail =
        blobDetail ||
        parsedBody?.error_description ||
        parsedBody?.message ||
        parsedBody?.detail ||
        axiosErr.message ||
        t('failed-to-download-bundle');

      const hint =
        status === 405
          ? t('endpoint-not-deployed-hint')
          : status === 401 || status === 403
            ? t('not-authorized-hint')
            : undefined;

      setSubmitError({
        status,
        url: fullUrl || 'api/admin/licensing/on-prem-bundle',
        detail,
        hint
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Success view ──────────────────────────────────────────────────
  if (bundleDownloaded) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="medium"
        title={t('onprem-bundle-downloaded')}
        closeOnOutsideClick={false}
        closeOnEsc={false}
        footerActions={[
          <Button
            key="done"
            onClick={onClose}
            label={t('done')}
            size="small"
            variant="primary"
          />
        ]}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-400/10 dark:bg-green-400/15">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5 text-green-600 dark:text-green-400" />
            <p className="text-sm text-green-800 dark:text-green-300">
              {t('onprem-bundle-download-started', {
                filename: bundleFilename
              })}
            </p>
          </div>

          <div
            className="p-4 rounded-2xl border border-hairline text-sm space-y-2"
            style={{ color: getColour('normalText') }}
          >
            <p className="font-medium">{t('onprem-bundle-zip-contains')}</p>
            <ul
              className="list-disc list-inside space-y-1 text-xs font-mono"
              style={{ color: getColour('inactiveText') }}
            >
              <li>license.json</li>
              <li>license_key.pub</li>
              {bundleHadRealtime && <li>license.dat</li>}
            </ul>
          </div>

          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('onprem-bundle-install-note')}
          </p>
        </div>
      </Modal>
    );
  }

  // ─── Form view ─────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title={t('download-onprem-bundle')}
      caption={t('download-onprem-bundle-caption')}
      footerActions={[
        <Button
          key="cancel"
          onClick={onClose}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="download"
          onClick={handleSubmit(onSubmit)}
          label={isSubmitting ? t('downloading-bundle') : t('download-bundle')}
          icon={<Package size={14} />}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      <form className="space-y-4">
        {submitError && (
          <div
            className="flex items-start gap-3 p-3 rounded-2xl bg-red-500/10 border border-red-500/25 dark:bg-red-500/15 dark:border-red-500/30"
            role="alert"
          >
            <AlertOctagon className="h-5 w-5 flex-shrink-0 mt-0.5 text-red-600 dark:text-red-400" />
            <div className="min-w-0 flex-1 text-sm">
              <div className="font-semibold text-red-800 dark:text-red-300">
                {submitError.status
                  ? `${submitError.status} — ${t('failed-to-download-bundle')}`
                  : t('failed-to-download-bundle')}
              </div>
              <div className="mt-0.5 break-words text-red-800 dark:text-red-300">
                {submitError.detail}
              </div>
              {submitError.hint && (
                <div className="mt-1.5 text-xs text-red-700 dark:text-red-400">
                  {submitError.hint}
                </div>
              )}
              <div className="mt-1.5 text-xs font-mono break-all opacity-80 text-red-700 dark:text-red-400">
                POST {submitError.url}
              </div>
            </div>
          </div>
        )}

        <Input
          id="licensee"
          label={t('licensee')}
          size="small"
          required
          placeholder={t('licensee-placeholder')}
          error={errors.licensee?.message}
          {...register('licensee', {
            required: t('licensee-required') || 'Licensee is required',
            minLength: {
              value: 4,
              message:
                t('licensee-min-length') ||
                'Licensee must be at least 4 characters'
            }
          })}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="org_limit"
            label={t('org-limit')}
            type="number"
            min={1}
            size="small"
            {...register('org_limit', { valueAsNumber: true, min: 1 })}
          />
          <Input
            id="user_limit"
            label={t('user-limit')}
            type="number"
            min={1}
            size="small"
            {...register('user_limit', { valueAsNumber: true, min: 1 })}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            id="max_processing"
            label={t('max-processing')}
            type="number"
            min={1}
            size="small"
            {...register('max_processing', { valueAsNumber: true, min: 1 })}
          />
          <Controller
            name="expires"
            control={control}
            rules={{
              validate: v =>
                !v ||
                (v >= expiresMin && v <= expiresMax) ||
                t('expiry-out-of-range')
            }}
            render={({ field }) => (
              <DateInput
                id="expires"
                label={t('expires')}
                size="small"
                value={field.value || ''}
                onChange={v => field.onChange(v)}
                placeholder={t('no-expiry-placeholder') || 'No expiry'}
                min={expiresMin}
                max={expiresMax}
              />
            )}
          />
        </div>

        {/* Realtime */}
        <div className="border-t border-hairline pt-4 space-y-3">
          <Checkbox
            id="realtime"
            label={t('realtime-asr-add-on')}
            {...register('realtime')}
            variant="primary"
            wrapperClass="flex items-center gap-2"
            labelClass="text-sm font-medium"
          />
          <p
            className="text-xs ml-6 -mt-2"
            style={{ color: getColour('inactiveText') }}
          >
            {t('realtime-asr-help')}
          </p>

          {realtimeEnabled ? (
            <div className="space-y-3">
              <Input
                id="max_realtime_streams"
                label={t('max-realtime-streams')}
                type="number"
                min={1}
                size="small"
                wrapperClass="w-full sm:w-1/2"
                {...register('max_realtime_streams', {
                  setValueAs: v =>
                    v === '' || v === null || v === undefined ? null : Number(v)
                })}
                placeholder={t('max-realtime-streams-placeholder') || 'e.g. 4'}
              />
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label
                    className="block text-sm font-semibold pl-1"
                    style={{ color: getColour('normalText') }}
                  >
                    {t('realtime-languages')}
                  </label>
                  <button
                    type="button"
                    onClick={handleToggleSelectAll}
                    disabled={languagesLoading || languageOptions.length === 0}
                    className="text-xs underline hover:no-underline disabled:opacity-50"
                    style={{ color: getColour('activeText') }}
                  >
                    {allSelected ? t('clear-all') : t('select-all')}
                  </button>
                </div>
                <Controller
                  name="realtime_languages"
                  control={control}
                  render={({ field }) => (
                    <SelectInput
                      isMulti
                      isSearchable
                      placeholder={t('select-realtime-languages')}
                      options={languageOptions}
                      value={field.value}
                      onChange={selected =>
                        field.onChange(Array.isArray(selected) ? selected : [])
                      }
                    />
                  )}
                />
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
